import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, input, output } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { map, of, take } from 'rxjs';
import { MoovConfigurationStatus } from '../../models/moovConfigurationStatus';
import { MoovService } from '../../services/MoovService';

@Component({ standalone:false,
  selector: 'app-moovs-in-testing-status-table',
  templateUrl: './moovs-in-testing-status-table.component.html',
  styleUrls: ['./moovs-in-testing-status-table.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush
})
export class MoovsInTestingStatusTableComponent {
  readonly customerContext = input<number|undefined>(undefined);
  readonly moovConfigurationSelected = output<MoovConfigurationStatus>();

  public paginator?: MatPaginator;
  public sort?: MatSort;

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }
      
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }
      
  setDataSourceAttributes() {
    if(this._dataSource){
      this._dataSource.paginator = this.paginator;
      this._dataSource.sort = this.sort;
    }
    this.cd.detectChanges();
  }

  private _dataSource?: MatTableDataSource<MoovConfigurationStatus>;

  public busy:boolean=true;

  public pageSize: number = 50;

  constructor(private moovService:MoovService, private datepipe: DatePipe, private cd:ChangeDetectorRef){}

  ngOnInit(){
    const storedPageSize = localStorage.getItem("MoovsInTestingStatusTableComponentPageSize");
    let pageSize;
    if(storedPageSize) pageSize=parseInt(storedPageSize);
    if(pageSize !== undefined && !isNaN(pageSize)) this.pageSize=pageSize;
    this.cd.detectChanges()
    this.moovService.getMoovConfigurationsInStatus(true, 1).pipe(take(1)).subscribe((mcspArray:MoovConfigurationStatus[])=>{
      this.processMoovConfigurationsInStatus(mcspArray);
    });
  }

  ngOnDestroy(){
    localStorage.setItem("MoovsInTestingStatusTableComponentPageSize", this.pageSize.toString());
  }

  get displayedColumns(){
    return ['clientName', 'moovConfigurationDescription', 'moovName', 'lastAddedAt'];
  }

  get dataSource(){
    return this._dataSource;
  }

  private processMoovConfigurationsInStatus(mcs_array: MoovConfigurationStatus[]) {
    if (mcs_array.length === 0) {
      this.busy = false;
      this._dataSource = new MatTableDataSource<MoovConfigurationStatus>([]);
      this.setDataSourceAttributes();
      this.cd.detectChanges();
      return;
    }

    var currentExecuted:MoovConfigurationStatus[] = [];
    var pastExecuted:MoovConfigurationStatus[] = [];
    mcs_array.forEach(mcs => {
      if (mcs.lastExecutedOnOlderConfiguration) {
        pastExecuted.push(mcs);
      } else {
        currentExecuted.push(mcs);
      }
    });

    const buildDataSource = () => {
      this._dataSource = new MatTableDataSource(mcs_array);
      this.setDataSourceAttributes();
      this._dataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'clientName':
            return item.clientName?.toLocaleLowerCase() ?? '';

          case 'moovConfigurationDescription':
            return item.moovConfigurationDescription?.toLocaleLowerCase() ?? '';

          case 'moovName':
            return item.moovName?.toLocaleLowerCase() ?? '';

          case 'lastAddedAt':
            return item.lastAddedAt?.getTime() ?? 0;

          default: {
            const value = item[property as keyof MoovConfigurationStatus];

            if (value instanceof Date) {
              return value.getTime();
            }

            if (typeof value === 'boolean') {
              return value ? 1 : 0;
            }

            return value ?? '';
          }
        }
      };
      this.busy = false;
      this.cd.detectChanges();
    };

    if (pastExecuted.length > 0) {
      this.moovService.getMoovInstances(false, pastExecuted.map(mcs => mcs.lastExecutedMoovInstanceId)).pipe(
        take(1),
        map(mi_array => {
          mi_array.forEach(mi => {
            pastExecuted.find(p => p.lastExecutedMoovInstanceId === mi.id)!.lastAddedAt = mi.added;
          });
          buildDataSource();
        })
      ).subscribe();
    }
    else {
      buildDataSource();
    }
  }

  public getExecutionDate(mcs:MoovConfigurationStatus){
    return mcs.lastAddedAt?this.datepipe.transform(mcs.lastAddedAt, 'MM/dd/yyyy hh:mm:ss a'):'';
  }

  public selectionChanged(mcs:MoovConfigurationStatus){
    this.moovConfigurationSelected.emit(mcs);
  }

  pageChanged(event:any){
    this.pageSize=event.pageSize;
  }
}
